import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckoutSection from './utils/Checkout.Section';
import { translate } from '../../Services/translation';
import Dropdown from '../Dropdown';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { useDispatch } from 'react-redux';
import {
  setSelectedDeliveryAddress,
  setSelectedBillingAddress,
} from '../../Actions/Checkout.action';
import {
  setCustomerDetails,
  setAlternativeAddress,
  setShowAlternativeAddress,
} from '../../Actions/Checkout.action';
import { businessAlternativeAddressCustomerDetailsSchema } from './utils/Checkout.ValidationSchema';
import InfoContainer from './utils/Checkout.InfoContainer';
import RadioButton from '../Inputs/Radiobutton';
import { CustomerInfoInput } from './Checkout.CustomerInfoInput';

const BusinessCustomerInfo = () => {
  const dispatch = useDispatch();
  const { payload } = useTypedSelector((state) => state.checkout);
  const countriesInChannelList = globalThis.LitiumConstants.countriesInChannel;
  const countriesList = globalThis.LitiumConstants.countries.filter(x => x.custom == false);
  const [country, setCountry] = useState(countriesList[0].value);

  const addressList = useMemo(
    () =>
      payload.companyAddresses.filter(
        (type) => type.addressType !== 'Billing'
      ),
    [payload.companyAddresses]
  );
  const billingList = useMemo(
    () =>
      payload.companyAddresses.filter(
        (type) => type.addressType == 'Billing'
      ),
    [payload.companyAddresses]
  );
  const selectedDeliveryAddress = useMemo(
    () =>
      addressList.find(
        (a) => a.systemId == payload.selectedDeliveryAddressId
      ),
    [addressList, payload.selectedDeliveryAddressId]
  );
  const selectedBillingAddress = useMemo(
    () =>
      billingList.find(
        (a) => a.systemId == payload.selectedBillingAddressId
      ),
    [billingList, payload.selectedBillingAddressId]
  );
  const deliveryAddressCountry = useMemo(
    () =>
      countriesInChannelList.find(
        (x) => x.value == selectedDeliveryAddress?.country
      ),
    [selectedDeliveryAddress]
  );

  const billingAddressCountry = useMemo(
    () =>
      countriesInChannelList.find(
        (x) => x.value == selectedBillingAddress?.country
      ),
    [selectedBillingAddress]
  );
  const reference = payload.customerDetails;

  const firstName = reference.firstName ? `${reference.firstName} ` : '';
  const lastName = reference.lastName ?? '';
  const name = firstName + lastName;

  const onCustomerDetailsChange = useCallback(
    (stateKey, id, value) => {
      if (id == 'country') {
        setCountry(value);   
      }
      switch (stateKey) {
        case 'customerDetails':
          dispatch(setCustomerDetails(id, value));
          break;
        case 'alternativeAddress':
          dispatch(setAlternativeAddress(id, value));
          break;
      }
    },
    [dispatch]
  );

  return (
    <CheckoutSection className="flex flex-col gap-6">
      <h4 className="uppercase">
        {translate('checkout.customerinfo.title')}
      </h4>
      <InfoContainer
        name={name}
        phone={reference.phoneNumber}
        email={reference.email}
        icon="user"
        title={translate('checkout.order.reference')}
      />
      <div className="flex flex-col gap-2">
        <span className="text-lg font-bold">
          {translate('checkout.selectdelivery')}
        </span>
        <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
          <RadioButton
            label={translate('checkout.savedaddresses')}
            name="add-or-saved"
            onChange={() => dispatch(setShowAlternativeAddress(false))}
            checked={payload.showAlternativeAddress == false}
            inputSize="large"
          />
          <RadioButton
            label={translate('checkout.addalternativeaddress')}
            name="add-or-saved"
            onChange={() => dispatch(setShowAlternativeAddress(true))}
            checked={payload.showAlternativeAddress == true}
            inputSize="large"
          />
        </div>
      </div>

      {payload.showAlternativeAddress ? (
        <form className="grid grid-cols-2 gap-4">
          <CustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="alternativeAddress"
            id="name"
            name="name"
            autoComplete="name"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="alternativeAddress"
            id="name2"
            name="name"
            autoComplete="name"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="alternativeAddress"
            id="address"
            autoComplete="address-line1"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="alternativeAddress"
            id="address2"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="zipCode"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="city"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />

          <div className="col-span-2">
            <label className="form__label" htmlFor="country">
              <b>{translate('checkout.customerinfo.country')}</b>
            </label>
            <Dropdown
              options={countriesInChannelList.map(({ text, value }) => ({
                label: text,
                value,
              }))}
              onChange={(value) => {
                onCustomerDetailsChange(
                  'alternativeAddress',
                  'country',
                  value.value
                );
                onCustomerDetailsChange(
                  'customerDetails',
                  'country',
                  value.value
                );
              }}
              selectedValue={country}
            />
          </div>

          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="phoneNumber"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="email"
            validationSchema={
              businessAlternativeAddressCustomerDetailsSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <Dropdown
            placeholder={translate('checkout.selectdelivery')}
            selectedValue={payload.selectedDeliveryAddressId}
            options={addressList.map((address) => ({
              value: address.systemId,
              label: `${
                address.customData.shipToName ?? payload.companyName
              }, ${address.address}, ${address.city}, ${
                address.zipCode
              }, ${address.country}`,
            }))}
            onChange={({ value }) => {
              dispatch(setSelectedDeliveryAddress(value));
            }}
            disabled={addressList.length <= 1}
          />

          <InfoContainer
            company={
              selectedDeliveryAddress?.customData.shipToName ??
              payload.companyName
            }
            address={selectedDeliveryAddress?.address}
            zip={selectedDeliveryAddress?.zipCode}
            city={selectedDeliveryAddress?.city}
            country={deliveryAddressCountry?.text}
            icon="home-pin"
            title={translate('checkout.order.delivery')}
          />
        </div>
      )}
      <div className="flex flex-col gap-4">
        <Dropdown
          label={translate('checkout.selectbilling')}
          placeholder={translate('checkout.selectbilling')}
          selectedValue={payload.selectedBillingAddressId}
          options={billingList.map((address) => ({
            value: address.systemId,
            label: `${
              address.customData.shipToName ?? payload.companyName
            }, ${address.address}, ${address.city}, ${address.zipCode}, ${
              address.country
            }`,
          }))}
          onChange={({ value }) => setSelectedBillingAddress(value)}
          disabled={billingList.length <= 1}
        />
        <InfoContainer
          company={
            selectedBillingAddress?.customData.shipToName ??
            payload.companyName
          }
          address={selectedBillingAddress?.address}
          zip={selectedBillingAddress?.zipCode}
          city={selectedBillingAddress?.city}
          country={billingAddressCountry?.text}
          icon="mail"
          title={translate('checkout.order.billing')}
        />
      </div>
    </CheckoutSection>
  );
};

export default BusinessCustomerInfo;
